import React, { FC, ReactNode, useEffect, useState } from "react";
import { Group, Image, Layer, Line } from "react-konva";
import { useStoreSelector } from "../../../../../../../../hooks/StoreHooks";
import { Vector2d } from "konva/lib/types";
import { Color, Option } from "../../../../../../../models/door_configuration";
import { PortaSezionaleClassic } from "../door-models/porta_sezionale_classic";
import { ColorCode, DoorType, FinishType } from "../../../../../../../core/common_types";
import { PortaSezionalePlana } from "../door-models/porta_sezionale_plana";
import { PortaSezionaleFullVision } from "../door-models/porta_sezionale_full_vision";
import { DatiPannello } from "../../../../../../../core/common_draw_types";
import { toDrawUnit, Vector2dToPointsArray } from "../../../../../../../core/common_functions";
import { Vector } from "vecti";
import { useFinishes } from "../../../../../../../providers/finish_provider";
import { PortinaAffiancata } from "../accessories/portina_affiancata";
import { PROFONDITA_INVOLUCRO, PROFONDITA_INVOLUCRO_PORTINA_LAT, SPESSORE_STIPITE } from "../../../../../../../core/common_constants";
import { PortinaLateraleClassic } from "../door-models/portina_laterale_classic";
import { PortinaLateralePlana } from "../door-models/portina_laterale_plana";
import { PortinaLateraleVetrata } from "../door-models/portina_laterale_vetrata";

export const QuoteExternalDoor: FC<{children?: ReactNode, options: Option[]}> = (props) => {
    const {options} = props;
    const finishesProvider = useFinishes();
    const [colors, setColors] = useState<{door: HTMLImageElement|undefined, frame: HTMLImageElement|undefined, handle: HTMLImageElement|undefined}|undefined>();

    const {doorConfigurationBuilder} = useStoreSelector(store => store);

    const lpowner = toDrawUnit(doorConfigurationBuilder.configuration?.larghezzaForo ?? 0);
    const hpowner = toDrawUnit(doorConfigurationBuilder.configuration?.altezzaForo ?? 0);
    const hp = toDrawUnit(doorConfigurationBuilder.configuration?.portinaAffiancata?.altezza ?? 0);
    const lp = toDrawUnit(doorConfigurationBuilder.configuration?.portinaAffiancata?.larghezza ?? 0);    
    
    const findPorta = () => {
        if (doorConfigurationBuilder.configuration?.portinaAffiancata?.usaStessaFinitura) {
            let option = options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.porta);
            return option;
        } else {
            let option = options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.portinaAffiancata?.porta?.optionId);
            return option;
        }
    }

    const findModello = () => {        
        if (doorConfigurationBuilder.configuration?.portinaAffiancata?.usaStessaFinitura) {
            let option = options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.modello);
            return option;
        } else {
            let option = options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.portinaAffiancata?.modello?.optionId);
            return option;
        }
    }

    const findFinitura = () => {
        if (doorConfigurationBuilder.configuration?.portinaAffiancata?.usaStessaFinitura) {
            let option = options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.finitura);
            return option;
        } else {
            let option = options.find(o => o?.optionId == doorConfigurationBuilder.configuration?.portinaAffiancata?.finitura?.optionId);
            return option;
        }
    }

    const renderPorta = () => {
        let porta = findPorta();
        let modello = findModello();
        let finitura = findFinitura();

        if (porta?.name == DoorType.classic) return (
            <PortinaLateraleClassic
                hpdraw={hp} 
                lpdraw={lp} 
                modello={modello}
                doorColor={colors?.door}
                frameColor={colors?.frame}
                handleColor={colors?.handle}
            />
        );

        if (porta?.name == DoorType.plana && finitura?.name != FinishType.glasspanel) return (
            <PortinaLateralePlana
                hpdraw={hp} 
                lpdraw={lp} 
                doorColor={colors?.door}
                frameColor={colors?.frame}
                handleColor={colors?.handle}
            />
        );

        if (porta?.name == DoorType.full_vision || (porta?.name == DoorType.plana && finitura?.name == FinishType.glasspanel)) return (
            <PortinaLateraleVetrata
                hpdraw={hp} 
                lpdraw={lp} 
                doorColor={colors?.door}
                frameColor={colors?.frame}
                handleColor={colors?.handle}
            />
        );

        return null;
    }

    const loadColors = async () => {
        let door: HTMLImageElement|undefined;
        let frame: HTMLImageElement|undefined;
        let handle: HTMLImageElement|undefined;
        if (doorConfigurationBuilder.configuration?.portinaAffiancata?.usaStessaFinitura) {
            door = await finishesProvider?.getImgPreview(doorConfigurationBuilder.getExternalColorCode());
        } else {
            door = await finishesProvider?.getImgPreview(doorConfigurationBuilder.configuration?.portinaAffiancata?.coloreEsterno);
        }

        frame = await finishesProvider?.getImgPreview(doorConfigurationBuilder.configuration?.portinaAffiancata?.externalFrameColorCode);
        handle = await finishesProvider?.getImgPreview(doorConfigurationBuilder.configuration?.portinaAffiancata?.externalHandleColorCode);
        
        setColors({door, frame, handle});
    }

    useEffect(() => {
        loadColors();
    }, [doorConfigurationBuilder.configuration?.portinaAffiancata]);

    if (!colors) return null;

    return (
        <Layer>
            <Group x={lpowner+PROFONDITA_INVOLUCRO_PORTINA_LAT+(PROFONDITA_INVOLUCRO_PORTINA_LAT/2)} y={hpowner-hp}>
                {/* <Soffitto hg={configurationBuilder.altezzaGarage} lg={configurationBuilder.larghezzaGarage} /> */}
                <Parete hgDraw={hp} lgDraw={lp} />
                {/* <Foro pf={pf} h={hf} l={lf} /> */}
                {renderPorta()}
            </Group>            
        </Layer>
    );
}

const Parete: FC<{lgDraw: number, hgDraw: number}> = ({lgDraw, hgDraw}) => {
    const buildPolyParete = (): Vector2d[] => {
        let pt1: Vector2d = {x: 0, y: 0};
        let pt2: Vector2d = {x: 0, y: hgDraw};
        let pt3: Vector2d = {x: -PROFONDITA_INVOLUCRO_PORTINA_LAT, y: hgDraw};        
        let pt4: Vector2d = {x: -PROFONDITA_INVOLUCRO_PORTINA_LAT, y: -PROFONDITA_INVOLUCRO_PORTINA_LAT};
        let pt5: Vector2d = {x: lgDraw + (PROFONDITA_INVOLUCRO_PORTINA_LAT), y: -PROFONDITA_INVOLUCRO_PORTINA_LAT};
        let pt6: Vector2d = {x: lgDraw + (PROFONDITA_INVOLUCRO_PORTINA_LAT), y: hgDraw};
        let pt7: Vector2d = {x: lgDraw, y: hgDraw};
        let pt8: Vector2d = {x: lgDraw, y: 0};

        // let pt2: Vector2d = {x: -PROFONDITA_INVOLUCRO*2, y: hgDraw + PROFONDITA_INVOLUCRO};
        // let pt3: Vector2d = {x: -PROFONDITA_INVOLUCRO*2, y: -PROFONDITA_INVOLUCRO};
        // let pt5: Vector2d = {x: lgDraw + (PROFONDITA_INVOLUCRO*2), y: hgDraw + PROFONDITA_INVOLUCRO};
        // let pt6: Vector2d = {x: lgDraw, y: hgDraw};

        return [pt1, pt2, pt3, pt4, pt5, pt6, pt7, pt8];
    }

    return (
        <>
            {/* <Line points={Vector2dToPointsArray(buildPolyParete())} 
                strokeEnabled
                stroke={"000"}
                strokeWidth={1}
                fillEnabled
                fill={"#ddd"}
                closed
            /> */}

            {/*buildPolyParete().map((p, index) => (
                <>
                    <Circle
                        x={p.x}
                        y={p.y}
                        radius={4}
                        fill={"#f00"}
                    />
                    <Text
                        x={p.x}
                        y={p.y}
                        text={"P"+(index+1)}
                    />
                </>
            ))*/}
        </>
    );
}

const Foro: FC<{pf: Vector, l: number, h: number}> = ({l, h, pf}) => {

    const buildPolyStipiteSx = (): Vector2d[] => {
        let vecDir = pf.subtract(Vector.of([0, 0])).normalize();
        let vecPt2 = Vector.of([0, 0]).add(vecDir.multiply(SPESSORE_STIPITE));

        vecDir = pf.subtract(Vector.of([0, h])).normalize();
        let vecPt3 = Vector.of([0, h]).add(vecDir.multiply(SPESSORE_STIPITE));
        //vecPt3 = Vector.of([vecPt2.x, vecPt3.y]);

        let pt1: Vector2d = {x: 0, y: 0};
        let pt2: Vector2d = {x: vecPt2.x, y: vecPt2.y};
        let pt3: Vector2d = {x: vecPt3.x, y: vecPt3.y}
        let pt4: Vector2d = {x: 0, y: h};

        return [pt1, pt2, pt3, pt4];
    }
        
    const buildPolyStipiteDx = (): Vector2d[] => {
        let vecDir = pf.subtract(Vector.of([l, h])).normalize();
        let vecPt3 = Vector.of([l, h]).add(vecDir.multiply(SPESSORE_STIPITE));

        vecDir = pf.subtract(Vector.of([l, 0])).normalize();
        let vecPt4 = Vector.of([l, 0]).add(vecDir.multiply(SPESSORE_STIPITE));

        //vecPt3 = Vector.of([vecPt4.x, vecPt3.y]);

        let pt1: Vector2d = {x: l, y: 0};
        let pt2: Vector2d = {x: l, y: h};
        let pt3: Vector2d = {x: vecPt3.x, y: vecPt3.y};
        let pt4: Vector2d = {x: vecPt4.x, y: vecPt4.y}

        return [pt1, pt2, pt3, pt4];
    }
    
    const buildPolyArchitrave = (): Vector2d[] => {
        let vecDir = pf.subtract(Vector.of([l, 0])).normalize();
        let vecPt3 = Vector.of([l, 0]).add(vecDir.multiply(SPESSORE_STIPITE));

        vecDir = pf.subtract(Vector.of([0, 0])).normalize();
        let vecPt4 = Vector.of([0, 0]).add(vecDir.multiply(SPESSORE_STIPITE));
        
        let pt1: Vector2d = {x: 0, y: 0};
        let pt2: Vector2d = {x: l, y: 0};
        let pt3: Vector2d = {x: vecPt3.x, y: vecPt3.y};
        let pt4: Vector2d = {x: vecPt4.x, y: vecPt4.y};

        return [pt1, pt2, pt3, pt4];
    }

    return (
        <>
            {/* <Rect
                x={0} 
                y={0} 
                width={l} 
                height={h} 
                stroke={"#000"} 
                strokeWidth={DEFAULT_STROKE_WIDTH}
            /> */}

            <Line points={Vector2dToPointsArray(buildPolyStipiteSx())} 
                strokeEnabled
                stroke={"000"}
                strokeWidth={1}
                fillEnabled
                fill={"#aaa"}
                closed
            />

            <Line points={Vector2dToPointsArray(buildPolyStipiteDx())} 
                strokeEnabled
                stroke={"000"}
                strokeWidth={1}
                fillEnabled
                fill={"#aaa"}
                closed
            />

            <Line points={Vector2dToPointsArray(buildPolyArchitrave())} 
                strokeEnabled
                stroke={"000"}
                strokeWidth={1}
                fillEnabled
                fill={"#aaa"}
                closed
            />
        </>
    );
}